/**
 * Starting a View-As session. ONE implementation, three entry points.
 *
 * WHY THIS FILE EXISTS. There were three impersonation entries and they did not
 * agree:
 *
 *   Tenants list  "Open Workspace"  mint + redirect, written out longhand
 *   Tenant Detail "Login as"        mint + redirect, written out longhand again
 *   Tenant Overview "View As User"  dispatched a `cluvik:view-as` CustomEvent
 *
 * That third one had NO LISTENER. `cluvik:view-as` appeared exactly once in the
 * whole Admin frontend — the dispatch — and there were no `addEventListener`
 * calls for any `cluvik:*` event anywhere. Confirmed live: the button fires the
 * event and no `support/action` request is made. It did nothing at all, and had
 * presumably never worked.
 *
 * The fix is not to add the missing listener. A third mechanism for the same
 * operation is what produced a dead one; this is the shared authority the other
 * two were already independently re-deriving.
 *
 * THE TAB IS OPENED BY THE CALLER, NOT HERE, and that is deliberate. Browsers
 * block `window.open` once it is called from a resolved-promise continuation
 * rather than directly from the click, so the blank tab must be opened inside
 * the handler's own user-activation window and handed in. A helper that opened
 * it after awaiting the mint would be blocked as a popup — which is the same
 * silent no-op this file exists to end. `noopener` is deliberately omitted by
 * callers so the handle survives to be redirected; the destination is our own
 * first-party CRM.
 */

export const VIEW_AS = {
  /** Grant minted and the tab is on its way to the CRM. */
  OPENED: 'opened',
  /** The server answered, and the answer was no. */
  REFUSED: 'refused',
  /** The call itself failed — network, 5xx, timeout. */
  FAILED: 'failed',
};

/**
 * Where the CRM redeems a grant.
 *
 * The grant is one-time credential material, so it goes in the FRAGMENT rather
 * than the path. A fragment is never sent to the server, never lands in access
 * logs or a Referer header, and is excluded from the URL that analytics reads
 * as the page location. The CRM's View-As bootstrap captures it synchronously
 * and scrubs it before anything else observes the address.
 *
 * The path form is still accepted by the CRM for links already in flight, but
 * Admin must not mint any more of them.
 */
export const viewAsUrl = (crmUrl, grantToken) =>
  `${crmUrl}/impersonate#grant=${encodeURIComponent(grantToken)}`;

/**
 * Mint a one-time impersonation grant and send `tab` to the CRM with it.
 *
 * NEVER THROWS and never leaves an orphan blank tab: every failure path closes
 * the tab it was handed, because an operator staring at about:blank cannot tell
 * a refusal from a hang.
 *
 * THE DESTINATION IS THE SERVER'S, NOT OURS. There is deliberately no `crmUrl`
 * parameter and no default. It used to take one, and every caller let it fall
 * back to a build-time constant (`REACT_APP_CRM_URL || 'http://localhost:3000'`)
 * — one value for four environments. Admin writes the grant into the runtime
 * for the TENANT's environment, so a Live tenant's grant went to the Live
 * runtime while the browser was sent to the development CRM, which cannot see
 * it; the redeem was then refused as though the link were invalid. Reproduced
 * live 7 Sep 2026.
 *
 * The mint response now carries `crm_base_url`, resolved server-side from the
 * tenant's own environment through the environment registry. We use that and
 * nothing else: a destination this file could choose is a destination an
 * attacker-influenced caller could choose.
 *
 * @param {object}   o
 * @param {Window}   o.tab        blank tab opened inside the click gesture
 * @param {Function} o.post       (path, body) => Promise<{data}>
 * @param {string}   o.tenantId
 * @param {string}   [o.userId]   impersonate this user; omit for the tenant's primary admin
 * @param {string}   o.reason     recorded in the audit trail on both sides
 * @returns {Promise<{outcome: string, message: string, email?: string}>}
 */
export const startViewAs = async ({ tab, post, tenantId, userId, reason }) => {
  const body = { action: 'IMPERSONATE', reason };
  if (userId) body.parameters = { user_id: userId };

  let data;
  try {
    ({ data } = await post(`/tenants/${tenantId}/support/action`, body));
  } catch (err) {
    if (tab) tab.close();
    return {
      outcome: VIEW_AS.FAILED,
      message: err?.response?.data?.detail || 'Could not start View As. Nothing has changed.',
    };
  }

  if (!data || !data.success || !data.grant_token) {
    // A 2xx that carries no grant is a refusal, not a success. Treating it as
    // one would send the operator to a CRM that has no idea who they are.
    if (tab) tab.close();
    return {
      outcome: VIEW_AS.REFUSED,
      message: (data && data.message) || 'Could not start View As for this tenant.',
    };
  }

  if (!data.crm_base_url) {
    // A grant with nowhere to redeem it. The server refuses before minting
    // when it cannot resolve a destination, so reaching here means an older
    // backend answered — and guessing a destination is exactly the defect
    // this file stopped having. Fail visibly instead.
    if (tab) tab.close();
    return {
      outcome: VIEW_AS.REFUSED,
      message: 'Could not start View As: the server did not say which environment to open.',
    };
  }

  const target = viewAsUrl(data.crm_base_url, data.grant_token);
  if (tab) {
    tab.location.href = target;
  } else {
    // The synchronous open was itself blocked. Rare, but falling back beats
    // leaving the operator with a silent no-op.
    window.open(target, '_blank', 'noopener,noreferrer');
  }
  return {
    outcome: VIEW_AS.OPENED,
    message: data.message || 'Opening the workspace',
    email: data.target_user_email,
  };
};
